import { useState, useEffect, useRef } from "react";

/* ============================================================
   NMS Login — animated network-topology background
   Palette matched to the dashboard: navy #1b4f7c header blue,
   action blue #2f80ed, slate greys, white cards.
   ============================================================ */

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:4000/api";

const styles = `
  .nms-root {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(160deg, #0e2a44 0%, #123c60 55%, #1b4f7c 100%);
    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    position: relative;
    overflow: hidden;
  }
  .nms-canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }
  .nms-card {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 400px;
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0 24px 60px rgba(4, 18, 33, 0.45);
    padding: 40px 36px 32px;
    margin: 24px;
    animation: cardIn 650ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  @keyframes cardIn {
    from { opacity: 0; transform: translateY(26px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .nms-field { animation: fieldIn 500ms ease both; }
  .nms-field:nth-of-type(1) { animation-delay: 180ms; }
  .nms-field:nth-of-type(2) { animation-delay: 300ms; }
  .nms-actions { animation: fieldIn 500ms ease both; animation-delay: 420ms; }
  @keyframes fieldIn {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .nms-logo {
    width: 54px; height: 54px;
    border-radius: 12px;
    background: linear-gradient(135deg, #1b4f7c, #2f80ed);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 14px;
    box-shadow: 0 8px 20px rgba(47, 128, 237, 0.35);
  }
  .nms-logo svg { animation: pulseGlow 2.6s ease-in-out infinite; }
  @keyframes pulseGlow {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.65; }
  }
  .nms-title {
    margin: 0; text-align: center;
    font-size: 22px; font-weight: 600; color: #163a5c;
    letter-spacing: 0.2px;
  }
  .nms-sub {
    margin: 6px 0 26px; text-align: center;
    font-size: 13px; color: #6b7c8f;
  }
  .nms-label {
    display: block; font-size: 13px; font-weight: 600;
    color: #2c3e50; margin-bottom: 6px;
  }
  .nms-input-wrap { position: relative; margin-bottom: 18px; }
  .nms-input {
    width: 100%; box-sizing: border-box;
    padding: 11px 14px 11px 40px;
    font-size: 14px; color: #1f2d3a;
    background: #f7f9fb;
    border: 1px solid #d3dbe3;
    border-radius: 6px;
    outline: none;
    transition: border-color 180ms ease, box-shadow 180ms ease, background 180ms ease;
  }
  .nms-input::placeholder { color: #9aa8b5; }
  .nms-input:focus {
    border-color: #2f80ed;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(47, 128, 237, 0.18);
  }
  .nms-input-icon {
    position: absolute; left: 12px; top: 50%;
    transform: translateY(-50%);
    color: #8fa2b3; pointer-events: none;
    display: flex;
    transition: color 180ms ease;
  }
  .nms-input-wrap:focus-within .nms-input-icon { color: #2f80ed; }
  .nms-eye {
    position: absolute; right: 10px; top: 50%;
    transform: translateY(-50%);
    background: none; border: none; cursor: pointer;
    color: #8fa2b3; padding: 4px; display: flex;
  }
  .nms-eye:hover { color: #2f80ed; }
  .nms-eye:focus-visible { outline: 2px solid #2f80ed; border-radius: 4px; }
  .nms-btn {
    width: 100%;
    padding: 12px;
    font-size: 15px; font-weight: 600;
    color: #ffffff;
    background: #2f80ed;
    border: none; border-radius: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: background 180ms ease, transform 120ms ease, box-shadow 180ms ease;
  }
  .nms-btn:hover { background: #2568c4; box-shadow: 0 6px 16px rgba(47,128,237,0.35); }
  .nms-btn:active { transform: scale(0.98); }
  .nms-btn:focus-visible { outline: 2px solid #163a5c; outline-offset: 2px; }
  .nms-btn:disabled { cursor: default; background: #6ea6ec; }
  .nms-btn .shine {
    position: absolute; top: 0; left: -80%;
    width: 50%; height: 100%;
    background: linear-gradient(105deg, transparent, rgba(255,255,255,0.35), transparent);
    animation: shine 3.2s ease-in-out infinite;
  }
  @keyframes shine {
    0%, 55% { left: -80%; }
    75%, 100% { left: 130%; }
  }
  .nms-spinner {
    width: 18px; height: 18px;
    border: 2.5px solid rgba(255,255,255,0.4);
    border-top-color: #ffffff;
    border-radius: 50%;
    display: inline-block;
    vertical-align: -4px;
    margin-right: 8px;
    animation: spin 700ms linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .nms-error {
    background: #fdecec; color: #b7362e;
    border: 1px solid #f3c1bd;
    font-size: 13px;
    padding: 9px 12px; border-radius: 6px;
    margin-bottom: 16px;
    animation: shake 380ms ease;
  }
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-6px); }
    50% { transform: translateX(6px); }
    75% { transform: translateX(-3px); }
  }
  .nms-success {
    text-align: center; padding: 24px 0 8px;
    animation: cardIn 500ms ease both;
  }
  .nms-check {
    width: 60px; height: 60px; margin: 0 auto 14px;
    border-radius: 50%;
    background: #eaf6ee;
    display: flex; align-items: center; justify-content: center;
  }
  .nms-check svg path {
    stroke-dasharray: 40;
    stroke-dashoffset: 40;
    animation: draw 600ms ease 200ms forwards;
  }
  @keyframes draw { to { stroke-dashoffset: 0; } }
  .nms-footer {
    margin-top: 22px; text-align: center;
    font-size: 12px; color: #8fa2b3;
  }
  @media (prefers-reduced-motion: reduce) {
    .nms-card, .nms-field, .nms-actions, .nms-error, .nms-success { animation: none; }
    .nms-btn .shine, .nms-logo svg { animation: none; }
    .nms-syslog-col, .nms-flow, .nms-led, .nms-pkt { animation: none !important; }
  }

  /* --- rsyslog stream: faint log lines drifting up the background --- */
  .nms-syslog-stream {
    position: absolute; top: 0; bottom: 0;
    width: 330px;
    overflow: hidden;
    z-index: 1;
    pointer-events: none;
    -webkit-mask-image: linear-gradient(to bottom, transparent, #000 18%, #000 82%, transparent);
    mask-image: linear-gradient(to bottom, transparent, #000 18%, #000 82%, transparent);
  }
  .nms-syslog-stream.left  { left: 2%; }
  .nms-syslog-stream.right { right: 2%; text-align: right; }
  .nms-syslog-col {
    font-family: 'Consolas', 'Courier New', monospace;
    font-size: 11px;
    line-height: 2.1;
    color: rgba(150, 205, 255, 0.16);
    white-space: nowrap;
    animation: syslogScroll 26s linear infinite;
  }
  .nms-syslog-stream.right .nms-syslog-col { animation-duration: 34s; }
  .nms-syslog-col .sev-warn { color: rgba(255, 205, 120, 0.20); }
  .nms-syslog-col .sev-err  { color: rgba(255, 140, 130, 0.20); }
  @keyframes syslogScroll {
    from { transform: translateY(0); }
    to   { transform: translateY(-50%); }
  }
  @media (max-width: 1100px) {
    .nms-syslog-stream { display: none; }
  }
  @media (max-width: 420px) {
    .nms-card { padding: 28px 20px 24px; margin: 16px; }
    .nms-title { font-size: 19px; }
  }

  /* --- rsyslog collector diagram under the card footer --- */
  .nms-rsyslog {
    margin-top: 18px;
    display: flex; justify-content: center;
    animation: fieldIn 500ms ease both;
    animation-delay: 540ms;
  }
  .nms-flow {
    stroke: #9fc3ea;
    stroke-width: 1.4;
    stroke-dasharray: 4 5;
    animation: flowDash 1.1s linear infinite;
  }
  @keyframes flowDash {
    to { stroke-dashoffset: -18; }
  }
  .nms-led {
    animation: ledBlink 1.4s steps(1) infinite;
  }
  @keyframes ledBlink {
    0%, 60% { fill: #3ddc84; }
    61%, 100% { fill: #1b7a49; }
  }
  .nms-pkt {
    fill: #2f80ed;
    animation: pktMove 2.2s ease-in-out infinite;
  }
  .nms-pkt.p2 { animation-delay: 0.7s; }
  .nms-pkt.p3 { animation-delay: 1.4s; }
  @keyframes pktMove {
    0%   { opacity: 0; transform: translateX(0); }
    15%  { opacity: 1; }
    85%  { opacity: 1; }
    100% { opacity: 0; transform: translateX(54px); }
  }
`;

/* Animated network topology background — nodes drift slowly,
   links draw between nearby nodes, packets pulse along links. */
function NetworkCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let nodes = [];
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resize = () => {
      canvas.width = canvas.offsetWidth * devicePixelRatio;
      canvas.height = canvas.offsetHeight * devicePixelRatio;
      ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const W = () => canvas.offsetWidth;
    const H = () => canvas.offsetHeight;
    const COUNT = Math.min(46, Math.floor((W() * H()) / 26000));

    for (let i = 0; i < COUNT; i++) {
      nodes.push({
        x: Math.random() * W(),
        y: Math.random() * H(),
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        r: 1.5 + Math.random() * 2,
        phase: Math.random() * Math.PI * 2,
      });
    }

    const LINK_DIST = 150;
    let t = 0;

    const frame = () => {
      t += 0.016;
      ctx.clearRect(0, 0, W(), H());

      // links
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < LINK_DIST) {
            const alpha = (1 - d / LINK_DIST) * 0.35;
            ctx.strokeStyle = `rgba(120, 180, 240, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();

            // packet traveling along the link
            if (!reduced && (i + j) % 7 === 0) {
              const p = (Math.sin(t * 1.2 + a.phase) + 1) / 2;
              const px = a.x + (b.x - a.x) * p;
              const py = a.y + (b.y - a.y) * p;
              ctx.fillStyle = `rgba(140, 200, 255, ${alpha * 2})`;
              ctx.beginPath();
              ctx.arc(px, py, 1.8, 0, Math.PI * 2);
              ctx.fill();
            }
          }
        }
      }

      // nodes
      for (const n of nodes) {
        const glow = reduced ? 0.7 : 0.55 + 0.35 * Math.sin(t * 1.6 + n.phase);
        ctx.fillStyle = `rgba(150, 205, 255, ${glow})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();

        if (!reduced) {
          n.x += n.vx;
          n.y += n.vy;
          if (n.x < 0 || n.x > W()) n.vx *= -1;
          if (n.y < 0 || n.y > H()) n.vy *= -1;
        }
      }

      if (!reduced) raf = requestAnimationFrame(frame);
    };
    frame();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="nms-canvas" aria-hidden="true" />;
}

/* Faint rsyslog-style log lines scrolling up on both sides of the screen */
const LOG_LINES = [
  ["info", "Jul 31 09:12:04 core-rtr1 sshd[2214]: Accepted session for admin"],
  ["info", "Jul 31 09:12:07 nas-02 radiusd: Acct-Start user=arjun nas=10.0.4.2"],
  ["warn", "Jul 31 09:12:11 sw-agg-3 lldpd: neighbor change on Gi1/0/24"],
  ["info", "Jul 31 09:12:13 bng-01 pppd: session up ifid=ppp0 ip=100.64.2.17"],
  ["err",  "Jul 31 09:12:18 core-rtr2 bgpd: peer 172.16.9.1 hold timer expired"],
  ["info", "Jul 31 09:12:21 rsyslogd: action queue enqueued 128 msgs"],
  ["info", "Jul 31 09:12:24 nas-02 radiusd: Acct-Update user=meera octets=884213"],
  ["warn", "Jul 31 09:12:29 fw-edge iptables: rate-limit hit on dpt=443"],
  ["info", "Jul 31 09:12:31 olt-05 dhcpd: lease renewed 10.20.7.44 mac=b4:2e"],
  ["info", "Jul 31 09:12:36 rsyslogd: forwarding to collector 10.0.0.9:514"],
  ["err",  "Jul 31 09:12:40 sw-agg-1 stpd: topology change detected vlan 120"],
  ["info", "Jul 31 09:12:44 nas-03 radiusd: Acct-Stop user=rohit time=3640s"],
];

function SyslogStream({ side }) {
  // duplicate the list so the 50% translateY loop is seamless
  const lines = [...LOG_LINES, ...LOG_LINES];
  return (
    <div className={`nms-syslog-stream ${side}`} aria-hidden="true">
      <div className="nms-syslog-col">
        {lines.map(([sev, text], i) => (
          <div key={i} className={sev === "warn" ? "sev-warn" : sev === "err" ? "sev-err" : ""}>
            {text}
          </div>
        ))}
      </div>
    </div>
  );
}

/* Small animated diagram: network devices streaming logs into an rsyslog collector */
function RsyslogDiagram() {
  return (
    <div className="nms-rsyslog" aria-hidden="true">
      <svg width="230" height="74" viewBox="0 0 230 74" fill="none">
        {/* device nodes */}
        <g stroke="#7d94a9" strokeWidth="1.5">
          <rect x="6" y="6" width="34" height="16" rx="3" fill="#f2f6fa" />
          <rect x="6" y="29" width="34" height="16" rx="3" fill="#f2f6fa" />
          <rect x="6" y="52" width="34" height="16" rx="3" fill="#f2f6fa" />
        </g>
        <text x="23" y="17" textAnchor="middle" fontSize="7.5" fill="#5b7185" fontFamily="Consolas, monospace">RTR</text>
        <text x="23" y="40" textAnchor="middle" fontSize="7.5" fill="#5b7185" fontFamily="Consolas, monospace">NAS</text>
        <text x="23" y="63" textAnchor="middle" fontSize="7.5" fill="#5b7185" fontFamily="Consolas, monospace">SW</text>

        {/* animated flow lines */}
        <path className="nms-flow" d="M42 14 C 80 14, 90 33, 128 35" />
        <path className="nms-flow" d="M42 37 L 128 37" />
        <path className="nms-flow" d="M42 60 C 80 60, 90 41, 128 39" />

        {/* packets riding the middle line */}
        <circle className="nms-pkt"    cx="58" cy="37" r="2.4" />
        <circle className="nms-pkt p2" cx="58" cy="37" r="2.4" />
        <circle className="nms-pkt p3" cx="58" cy="37" r="2.4" />

        {/* rsyslog collector server */}
        <rect x="132" y="18" width="52" height="38" rx="4" fill="#1b4f7c" stroke="#2f80ed" strokeWidth="1.5" />
        <line x1="138" y1="28" x2="166" y2="28" stroke="#8fb8e0" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="138" y1="36" x2="172" y2="36" stroke="#8fb8e0" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="138" y1="44" x2="160" y2="44" stroke="#8fb8e0" strokeWidth="1.5" strokeLinecap="round" />
        <circle className="nms-led" cx="176" cy="25" r="2.5" />
        <text x="158" y="68" textAnchor="middle" fontSize="8" fill="#7d94a9" fontFamily="Consolas, monospace">rsyslog :514</text>
      </svg>
    </div>
  );
}

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5" />
  </svg>
);

const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="4" y="10" width="16" height="11" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

const EyeIcon = ({ off }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
    {off && <line x1="4" y1="4" x2="20" y2="20" />}
  </svg>
);

export default function LoginPage({ onLoginSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    document.title = "BMS Log";
  }, []);

  const handleLogin = async () => {
    setError("");
    if (!username.trim() || !password) {
      setError("Enter both username and password to sign in.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ USER_NAME: username, PASSWORD: password }),
      });
      const data = await res.json();

      if (data.Status !== "Success") {
        setError(data.message || "Login failed");
        setLoading(false);
        return;
      }

      setLoading(false);
      setSuccess(true);
      setTimeout(() => onLoginSuccess(data.user), 900);
    } catch (err) {
      setLoading(false);
      setError(err.message);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <div className="nms-root">
      <style>{styles}</style>
      <NetworkCanvas />
      <SyslogStream side="left" />
      <SyslogStream side="right" />

      <div className="nms-card">
        {success ? (
          <div className="nms-success">
            <div className="nms-check">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
                <path d="M4 12.5l5 5L20 7" stroke="#2e9e5b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="nms-title">Signed in</h2>
            <p className="nms-sub">Loading your NMS dashboard…</p>
          </div>
        ) : (
          <>
            <div className="nms-logo">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                {/* log document */}
                <path d="M6 3h8l4 4v14H6z" />
                <path d="M14 3v4h4" />
                {/* log lines */}
                <line x1="9" y1="11" x2="15" y2="11" />
                <line x1="9" y1="14.5" x2="15" y2="14.5" />
                <line x1="9" y1="18" x2="12.5" y2="18" />
                {/* incoming syslog stream */}
                <path d="M1.5 9.5h2.5M1 13h3M1.5 16.5h2.5" strokeWidth="1.5" />
              </svg>
            </div>
            <h1 className="nms-title">BMS Log</h1>
            <p className="nms-sub">Sign in to view accounting logs</p>

            {error && <div className="nms-error" role="alert">{error}</div>}

            <div className="nms-field">
              <label className="nms-label" htmlFor="nms-user">Username</label>
              <div className="nms-input-wrap">
                <span className="nms-input-icon"><UserIcon /></span>
                <input
                  id="nms-user"
                  className="nms-input"
                  placeholder="Enter username"
                  value={username}
                  autoComplete="username"
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyDown={onKeyDown}
                />
              </div>
            </div>

            <div className="nms-field">
              <label className="nms-label" htmlFor="nms-pass">Password</label>
              <div className="nms-input-wrap">
                <span className="nms-input-icon"><LockIcon /></span>
                <input
                  id="nms-pass"
                  className="nms-input"
                  type={showPass ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={onKeyDown}
                />
                <button
                  type="button"
                  className="nms-eye"
                  aria-label={showPass ? "Hide password" : "Show password"}
                  onClick={() => setShowPass(!showPass)}
                >
                  <EyeIcon off={showPass} />
                </button>
              </div>
            </div>

            <div className="nms-actions">
              <button className="nms-btn" onClick={handleLogin} disabled={loading}>
                {!loading && <span className="shine" />}
                {loading ? (<><span className="nms-spinner" />Signing in…</>) : "Sign in"}
              </button>
            </div>

            <div className="nms-footer">Bits Management System · Authorized access only</div>
            <RsyslogDiagram />
          </>
        )}
      </div>
    </div>
  );
}
